<template>
  <ResponsiveLayout
    sticky-actions
    :progress="progress"
    :show-lang-picker="showLangPicker"
  >
    <template
      v-if="canGoBack"
      #header_start
    >
      <ButtonBack @click="emit('back')" />
    </template>
    <template #header_middle>
      <div
        class="kds-typography-title-medium"
        v-html="$t('b2b_self_test_generic_assessment')"
        v-if="showHeaderTitle"
      />
    </template>
    <div class="kds-main-wrapper">
      <div class="kds-content-block">
        <h3
          class="kds-typography-title-xlarge"
          v-html="painLocationTitle"
        />
        <div
          class="kds-typography-paragraph-large"
          v-html="$t('b2b_self_test_worst_pain_location_subtitle')"
          v-if="!env.isMskEu"
        />
      </div>
      <div class="worst-pain-location self-test">
        <div class="person-wrap">
          <PainLocations
            :body-parts="[worstPainLocation]"
            :css-classes="['images', 'non-interactive', 'transparent']"
          />
        </div>
        <fieldset class="form-container">
          <div
            v-for="painLocation in painLocationListParts"
            :key="painLocation.key"
            :class="[
              'quiz-radio',
              { 'quiz-radio--checked': isPainLocationSelected(painLocation) },
            ]"
            @click="handlePainLocationChange(painLocation)"
          >
            <label class="quiz-radio__label kds-typography-title-medium">
              {{ $t(painLocation.label) }}
            </label>
            <div class="quiz-radio__layout">
              <CustomRadio
                :checked="isPainLocationSelected(painLocation)"
                :name="painLocation.key"
                :data-qa-value="painLocation.key"
                :value="painLocation.key"
                data-qa="pain-location"
              />
            </div>
          </div>
        </fieldset>
      </div>
    </div>

    <template #actions>
      <BaseButton
        :disabled="!isValid"
        :text="$t('generic_action_continue')"
        data-qa="submit"
        @click="next"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import CustomRadio from '@shared/components/CustomRadio.vue'
import PainLocations from '@shared/components/PainLocations.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import { computed, ref } from 'vue'
import { useI18n } from 'petite-vue-i18n'
import env from '@shared/env.js'
import useSelfTestStore from '@shared/modules/assessment/stores/useSelfTestStore.js'
import usePreSubscriptionWebOptimizedFunnel from '@shared/composables/usePreSubscriptionWebOptimizedFunnel'
import { preloadImage } from '@shared/utils/preloadImage'
import { PAIN_LOCATION_OPTIONS } from '@shared/modules/assessment/config/selfTestOptions'

const selfTestStore = useSelfTestStore()
const preSubscriptionWebOptimizedFunnel = usePreSubscriptionWebOptimizedFunnel()
const { t } = useI18n()

const showHeaderTitle = computed(() => !preSubscriptionWebOptimizedFunnel.value)

// The self test hides the language picker: each answer is stamped with the
// language it was read in. This screen opens the MSK EU web funnel, before any
// answer, so the visitor can still pick a language here.
const showLangPicker = computed(
  () => env.isMskEu && preSubscriptionWebOptimizedFunnel.value,
)

const props = defineProps({
  progress: {
    type: Number,
    default: null,
  },
  handleNextEvent: {
    type: Function,
    default: () => {},
  },
  canGoBack: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['back'])

const worstPainLocation = ref(selfTestStore.worstPainLocation)

// MSK EU shows the web funnel's screen in every funnel: its header, no subtitle.
const painLocationTitle = computed(() =>
  env.isMskEu
    ? t('eu__self_test_pain_location_header__web')
    : t('b2b_self_test_pain_location_header'),
)

const isValid = computed(() => worstPainLocation.value.length > 0)

const painLocationListParts = PAIN_LOCATION_OPTIONS

const handlePainLocationChange = (painLocation) => {
  worstPainLocation.value = painLocation.key
  preloadImage(painLocation.image)
}

const isPainLocationSelected = (painLocation) =>
  worstPainLocation.value === painLocation.key

const next = () => {
  const option = PAIN_LOCATION_OPTIONS.find(
    (o) => o.key === worstPainLocation.value,
  )
  if (!option) return
  selfTestStore.answer('pain-location', {
    value: option.key,
    text: t(option.label),
    questionText: painLocationTitle.value,
  })
  props.handleNextEvent()
}
</script>

<style lang="scss">
@use '@shared/modules/assessment/views/css/SelfTest.scss' as *;

.worst-pain-location {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin: 0 auto;

  .person-wrap {
    margin-right: 0;
    width: 40%;
    box-sizing: border-box;
  }
}
</style>
